import {
  Scene,
  PerspectiveCamera,
  OrthographicCamera,
  WebGLRenderer,
  AxesHelper,
  GridHelper,
  MeshBasicMaterial,
  PlaneGeometry,
  Mesh,
  Vector3,
  Color,
  ShaderMaterial,
  TextureLoader
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
// Tom Patterson, www.shadedrelief.com
import textureUrl from '../assets/earth.jpg';
// Eric Gaba – (https://commons.wikimedia.org/wiki/User:Sting)
import textureTissotUrl from '../assets/tissot_equirectangular.png';

// init
var w = window.innerWidth;
var h = window.innerHeight;
const aspectRatio = w / h;
const scene = new Scene();
const perspCamera = new PerspectiveCamera(75, aspectRatio, 0.1, 1000);
const orhtoSize = 1.1;
const orthoCamera = new OrthographicCamera(
  (orhtoSize * aspectRatio) / 2,
  (orhtoSize * aspectRatio) / -2,
  orhtoSize / 2,
  orhtoSize / -2,
  0.01,
  1000
);

const cam = perspCamera;
cam.position.z = 3;
cam.lookAt(new Vector3(0, 0, 0));

const renderer = new WebGLRenderer({ useDevicePixelRatio: true });
renderer.setClearColor(new Color(0.1, 0.1, 0.1));
renderer.setSize(window.innerWidth, window.innerHeight);
document.body.appendChild(renderer.domElement);

const axesHelper = new AxesHelper(2);
axesHelper.position.x = 0.001;
scene.add(axesHelper);

// horizontal grid
const gridHelperH = new GridHelper(5, 10, 0x505050, 0x505050);
scene.add(gridHelperH);
gridHelperH.position.y = -0.001;
gridHelperH.position.y = -1;

// vertical grid z
const gridHelperVZ = new GridHelper(5, 10, 0x505050, 0x505050);
gridHelperVZ.geometry.rotateX(Math.PI / 2);
scene.add(gridHelperVZ);

// vertical grid x
// const gridHelperVX = new GridHelper(5, 10, 0x505050, 0x505050);
// gridHelperVX.geometry.rotateZ(Math.PI / 2);
// scene.add(gridHelperVX);

const controls = new OrbitControls(cam, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.1;
controls.rotateSpeed = 0.5;
controls.zoomSpeed = 0.5;

const vertShader = `
  #define PI 3.141592653589793
  #define PI2 6.283185307179586

  varying vec2 vUv;
  
  uniform float sphere;
  uniform float mercator;
  uniform float equi;

  void main() {
    vUv = uv;

    /*
     * position.x goes from -1...1
     * position.y goes from -0.5...0.5
     * position.z = 0
     */

    // lat/lng
    float lng = position.x * PI2;
    float lat = position.y * PI;

    // equirectangular
    // float xEqui = lng / PI2;
    // float yEqui = lat / PI;
    float xEqui = position.x;
    float yEqui = position.y;
    vec4 equiPos = vec4(xEqui, yEqui, 0.0, 1.0);

    // mercator
    float latClamped = clamp(lat, -1.4835298641951802, 1.4835298641951802);
    float yMerc = log(tan(PI / 4.0 + latClamped / 2.0)) / PI2;
    float xMerc = xEqui / 2.0;
    vec4 mercatorPos = vec4(xMerc, yMerc, 0.0, 1.0);

    // sphere
    float y = sin(lat);
    float c = sqrt(1.0 - pow(y, 2.0));
    float xSphere = sin(lng / 2.0) * c;
    float zSphere = cos(lng / 2.0) * c;
    float ySphere = y;
    vec4 spherePos = vec4(vec3(xSphere, ySphere, zSphere) * 0.5, 1.0);

    // scale and move to camera for better effect
    equiPos *= 2.0;
    equiPos.z = 0.5;
    equiPos.w = 1.0;
    mercatorPos *= 2.0;
    mercatorPos.z = 0.5;
    mercatorPos.w = 1.0;

    vec4 finalPos = (mercatorPos * mercator) + (equiPos * equi) + (spherePos * sphere);
    gl_Position = projectionMatrix * modelViewMatrix * finalPos;
  }`;

const fragShader = `
  varying vec2 vUv;
  uniform sampler2D texture;

  void main() {
    vec4 tcolor = texture2D( texture, vUv);
    gl_FragColor = tcolor;
  }
`;

const texture1 = new TextureLoader().load(textureUrl);
const texture2 = new TextureLoader().load(textureTissotUrl);
const material = new ShaderMaterial({
  uniforms: {
    texture: { value: texture1 },
    equi: { value: 0.0 },
    mercator: { value: 0.0 },
    sphere: { value: 1.0 }
  },
  vertexShader: vertShader,
  fragmentShader: fragShader,
  wireframe: false
});
const geometry = new PlaneGeometry(2, 1, 64, 64);
const plane = new Mesh(geometry, material);
scene.add(plane);

const slider = document.getElementById('slider');

// animation loop
function animate() {
  controls.update();
  requestAnimationFrame(animate);
  renderer.render(scene, cam);
}
animate();

// switch projections
let lastOne = 'sphere';
const buttons = ['sphere', 'equi', 'mercator'];

buttons.forEach(id =>
  document.getElementById(id).addEventListener('click', () => switchTo(id))
);

function switchTo(newParam) {
  buttons.forEach(id => (material.uniforms[id].value = 0));
  material.uniforms[lastOne].value = 1;
  transitionTo(newParam, 1.0);
  transitionTo(lastOne, 0.0);
  lastOne = newParam;
}

const transitionDamping = 10;

function transitionTo(param, value) {
  if (Math.abs(material.uniforms[param].value - value) < 0.0001) {
    material.uniforms[param].value = value;
    return;
  }

  requestAnimationFrame(() => {
    material.uniforms[param].value +=
      (value - material.uniforms[param].value) / transitionDamping;
    transitionTo(param, value);
  });
}

// change texture button
const textureButton = document.getElementById('texture');
textureButton.addEventListener('click', () => {
  const textureUniform = material.uniforms.texture;
  const isColor = textureUniform.value === texture1;
  textureUniform.value = isColor ? texture2 : texture1;
  textureButton.innerHTML = isColor ? 'Texture (Color)' : 'Texture (Tissot)';
});
